import { useSyncExternalStore } from 'react';

/**
 * Tracks Cmail's local selection state: the msgId of the email open in the
 * detail view, and the active category tab.
 *
 * V2's route-derived Concierge context (`route-context-source.ts`) only sees
 * `location.pathname`. Both the open email and the category tab are local
 * `CmailInbox` state, not route changes, so `matchRoute` can never see them.
 * This store lets `CmailInbox` publish them so `useDerivedScreenContext` can
 * fold the message id into the `cmail` entity context (OMNI-632) and the tab
 * into the screen context's `subTab`, which the chat agent's `/starters`
 * endpoint uses to return tab-specific conversation starters (OMNI-192).
 *
 * Backed by a globalThis singleton for the same reason as `dwell/nudgeStore`:
 * keeps a single instance across duplicate module evaluations under the test
 * runner. In the app there is only ever one instance.
 */
const KEY = '__cowbellCmailSelectionStore__';

interface Store {
  msgId: string | undefined;
  categoryTab: string | undefined;
  listeners: Set<() => void>;
}

const g = globalThis as unknown as Record<string, Store | undefined>;
const store: Store = (g[KEY] ??= {
  msgId: undefined,
  categoryTab: undefined,
  listeners: new Set(),
});

function emit() {
  store.listeners.forEach((l) => l());
}

export function setOpenCmailMessageId(msgId: string | undefined): void {
  if (store.msgId === msgId) return;
  store.msgId = msgId;
  emit();
}

export function setOpenCmailCategoryTab(tab: string | undefined): void {
  if (store.categoryTab === tab) return;
  store.categoryTab = tab;
  emit();
}

function subscribe(cb: () => void): () => void {
  store.listeners.add(cb);
  return () => {
    store.listeners.delete(cb);
  };
}

function getMsgId(): string | undefined {
  return store.msgId;
}

function getCategoryTab(): string | undefined {
  return store.categoryTab;
}

export function useOpenCmailMessageId(): string | undefined {
  return useSyncExternalStore(subscribe, getMsgId, getMsgId);
}

export function useOpenCmailCategoryTab(): string | undefined {
  return useSyncExternalStore(subscribe, getCategoryTab, getCategoryTab);
}
